/* NoiseDose — доза шума за смену по нормативу страны. Чистая арифметика,
 * без DOM: её делят /sound-meter (живой замер и калькулятор задач) и тесты
 * (tests/js/noise-dose.test.mjs, сверка с опубликованными примерами HSE,
 * OSHA и NIOSH).
 *
 * ── Почему нормативов несколько, а не «85 дБ» ─────────────────────────────
 * До 21.10.2026 шумомер считал дозу по одному NIOSH (85 дБА, обмен 3 дБ), а
 * плитку «TWA» подписывал «OSHA», хотя сама формула была энергетической, то
 * есть с обменом 3 дБ. У OSHA обмен 5 дБ: при 95 дБА OSHA разрешает 4 ч,
 * NIOSH — 47 мин, ISO 9612 / ЕС — 50 мин. Это одна и та же смена, а ответ
 * «сколько можно» отличается вчетверо — показать человеку не тот норматив
 * значит дать ему не тот ответ.
 *
 * Два семейства:
 *   · metric 'lex' — равная энергия (ISO 9612, ЕС 2003/10/EC, Великобритания,
 *     Австралия, Россия, Украина): LEX,8h = 10·lg(Σ Tᵢ/8 · 10^(Lᵢ/10)),
 *     порога интегрирования нет. Допустимое время — точная обратная формула.
 *   · metric 'twa' — правило обмена q дБ (OSHA и Корея q=5, NIOSH / Мексика /
 *     Индонезия q=3): T(L) = 8 / 2^((L − Lc)/q), доза D = 100·Σ Cᵢ/Tᵢ,
 *     TWA = Lc + (q/lg2)·lg(D/100). Уровни ниже порога интегрирования не
 *     считаются (у OSHA 80 дБА — программа сохранения слуха, 1910.95(d)(2)(i)).
 *
 * Пики C-взвешенные: телефон их измерить не может (микрофон упирается в свой
 * предел около 120 дБ SPL), поэтому пороги пиков здесь — справка, а не
 * сравнение с замером; см. sound-meter-dose.js.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    root.NoiseDose = api;
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this), function () {
    'use strict';

    var LOG2 = Math.log(2) / Math.LN10;

    /* marks: [уровень за 8 ч, вид]; вид — 'lower' | 'upper' | 'action' | 'limit'.
       peaks: [дБ(C), вид]. Источники — в src (показываются под выбором). */
    var STANDARDS = {
        eu: {
            criterion: 85, exchange: 3, threshold: null, metric: 'lex', points: true,
            marks: [[80, 'lower'], [85, 'upper'], [87, 'limit']],
            peaks: [[135, 'lower'], [137, 'upper'], [140, 'limit']],
            src: 'Directive 2003/10/EC art. 3; Control of Noise at Work Regulations 2005 (UK) reg. 4; ISO 9612:2009'
        },
        /* Украина — ДЕЙСТВУЮЩИЕ ДСН 3.3.6.037-99 (сверено 22.10.2026, dnaop.com — текст ДСН):
           табл. 2, п. 5 «постійні робочі місця у виробничих приміщеннях» — 80 дБА; п. 5.4 —
           максимум 110 дБА (коливний, переривчастий), 125 дБА (імпульсний), пика в дБС нет.
           Наказ МОЗ № 540 від 23.03.2023 (80/85/87 дБА, 135/137/140 дБС) набирає чинності лише
           через 6 міс. після воєнного стану (ligazakon) — до того его как норму не показываем. */
        ua: {
            criterion: 80, exchange: 3, threshold: null, metric: 'lex',
            marks: [[80, 'limit']],
            peaks: [],
            src: 'ДСН 3.3.6.037-99 (табл. 2: 80 дБА; п. 5.4: max 110 дБА, імпульсний 125 дБА). Наказ МОЗ № 540 від 23.03.2023 (80/85/87 дБА) — чинний через 6 міс. після воєнного стану'
        },
        au: {
            criterion: 85, exchange: 3, threshold: null, metric: 'lex',
            marks: [[85, 'limit']],
            peaks: [[140, 'limit']],
            src: 'Model WHS Regulations r. 56 (LAeq,8h 85 dB(A), LC,peak 140 dB(C)); NZ: HSW (GRWM) Regulations 2016'
        },
        ru: {
            criterion: 80, exchange: 3, threshold: null, metric: 'lex',
            marks: [[80, 'limit']],
            peaks: [[137, 'limit']],
            // Редакция от 24.12.2025 (в силе с 01.03.2026), п. 35 — нормы шума не изменились
            // (КонсультантПлюс, сверено 22.10.2026).
            src: 'СанПиН 1.2.3685-21 в ред. от 24.12.2025, п. 35 (LpAeq,8ч 80 дБА; LpC,peak 137 дБС; LpAS,max 110 дБА; LpAI,max 125 дБА)'
        },
        osha: {
            criterion: 90, exchange: 5, threshold: 80, metric: 'twa',
            marks: [[85, 'action'], [90, 'limit']],
            peaks: [[140, 'limit']],
            src: '29 CFR 1910.95 (PEL 90 dBA TWA, 5 dB; action level 85 dBA; Appendix A)'
        },
        niosh: {
            criterion: 85, exchange: 3, threshold: 80, metric: 'twa',
            marks: [[85, 'limit']],
            peaks: [[140, 'limit']],
            src: 'NIOSH Criteria for a Recommended Standard: Occupational Noise Exposure (DHHS 98-126)'
        },
        /* Корея — 고용노동부고시 제2018-24호 (2018.3.20), 별표 2, 표 2-1: 8 ч 90 · 4 ч 95 · 2 ч 100 ·
           1 ч 105 · 1/2 ч 110 · 1/4 ч 115 дБ(A), выше 115 — нельзя; 표 2-2 (импульсный): 100 ударов
           в день — 140 дБ(A), 1 000 — 130, 10 000 — 120 (копия текста уведомления, DGUV IFA, сверено
           22.10.2026). Порог интегрирования дозиметра (часто пишут «80 dB») первоисточником не
           подтвердился — не задаём (threshold: null). */
        kr: {
            criterion: 90, exchange: 5, threshold: null, metric: 'twa',
            marks: [[85, 'action'], [90, 'limit']],
            peaks: [[140, 'limit']],
            src: '고용노동부고시 제2018-24호 「화학물질 및 물리적 인자의 노출기준」 별표 2 (90 dB(A) 8시간, 5 dB; 충격소음 140 dB(A) 100회); 산업안전보건기준에 관한 규칙 제512조 (소음작업 85 dB)'
        },
        /* Бразилия — порог интегрирования NR-15 = 85 дБ(A), не 80 (80 — у NHO-01): заключение
           Fundacentro «Avaliação de ruído para fins de aposentadoria especial», парecer 3 (18.08.2022).
           Уровень действия — половина дозы: переходное положение NR-09 (ред. 2021, gov.br). */
        /* doseMarks: уровень действия NR-09 — «metade da dose», то есть 50 % дозы ПО NR-15
           (q = 5, порог 85). Отметка [80, 'action'] — её TWA-эквивалент для арифметики
           (TWA 80 ⇔ доза 50 %), но подписывать её уровнем «80 дБ(A)» нельзя: при пороге 85
           человек на 82 дБ(A) дозы не набирает вовсе. Подпись — в процентах дозы. */
        br: {
            criterion: 85, exchange: 5, threshold: 85, metric: 'twa', doseMarks: true,
            marks: [[80, 'action'], [85, 'limit']],
            peaks: [],
            src: 'NR-15 Anexo 1 (85 dB(A), q = 5, limiar de integração 85 dB(A) — parecer técnico Fundacentro, 2022); NR-09 (2021): nível de ação = metade da dose'
        },
        mx: {
            criterion: 90, exchange: 3, threshold: 80, metric: 'twa',
            marks: [[85, 'action'], [90, 'limit']],
            peaks: [],
            src: 'NOM-011-STPS-2001 (NER 90 dB(A) 8 h; TMPE = 8 / 2^((NER−90)/3))'
        },
        /* Индонезия — Permenaker 5/2018, Lampiran, «H. Nilai Ambang Batas Kebisingan»: 8 jam 85 ·
           4 jam 88 · 2 jam 91 · 1 jam 94 · 30 menit 97 … 0,11 detik 139 dBA — обмен 3 дБ подтверждён
           таблицей (jdih.kemnaker.go.id, сверено 22.10.2026). Порога интегрирования и пика в дБС
           в тексте нет — не задаём. */
        id: {
            criterion: 85, exchange: 3, threshold: null, metric: 'twa',
            marks: [[85, 'limit']],
            peaks: [],
            src: 'Permenaker No. 5 Tahun 2018, Lampiran H (NAB 85 dBA 8 jam, 88 dBA 4 jam, 91 dBA 2 jam)'
        }
    };

    var ORDER = ['eu', 'au', 'osha', 'niosh', 'ru', 'ua', 'kr', 'br', 'mx', 'id'];

    /* Норматив по умолчанию — по языку страницы. Испанский у нас латиноамериканский,
       а критерий 85 дБА / 3 дБ (NIOSH) — общий для большинства стран региона
       (Аргентина, Чили, Колумбия); Мексика выбирается в списке. */
    var BY_LOCALE = { en: 'niosh', ru: 'ru', uk: 'ua', de: 'eu', fr: 'eu', es: 'niosh', pt: 'br', ko: 'kr', id: 'id', ar: 'niosh' };

    function get(id) { return STANDARDS[id] || STANDARDS.niosh; }
    function defaultFor(locale) { return BY_LOCALE[locale] || 'niosh'; }
    function counted(std, L) { return std.threshold == null || L >= std.threshold; }

    /** Сколько часов в сутки допускает норматив при постоянном уровне L. */
    function allowedHours(id, L) {
        var std = get(id);
        if (!isFinite(L) || !counted(std, L)) return Infinity;
        if (std.metric === 'lex') return 8 / Math.pow(10, (L - std.criterion) / 10);
        return 8 / Math.pow(2, (L - std.criterion) / std.exchange);
    }

    /** Доза в процентах за отрезок hours при уровне L. */
    function partialDose(id, L, hours) {
        var T = allowedHours(id, L);
        if (!isFinite(T) || !(hours > 0)) return 0;
        return 100 * hours / T;
    }

    /** Суммарная доза по задачам [{level, hours}]. */
    function doseOf(id, segments) {
        var d = 0;
        for (var i = 0; i < segments.length; i++) d += partialDose(id, segments[i].level, segments[i].hours);
        return d;
    }

    /** 8-часовой показатель (LEX,8h или TWA) из дозы. Доза 0 → -Infinity. */
    function eightHour(id, dosePct) {
        var std = get(id);
        if (!(dosePct > 0)) return -Infinity;
        if (std.metric === 'lex') return std.criterion + 10 * Math.log10(dosePct / 100);
        return std.criterion + (std.exchange / LOG2) * Math.log10(dosePct / 100);
    }

    /** Самая высокая отметка норматива, которую показатель достиг (или null). */
    function verdict(id, value8h) {
        var std = get(id), hit = null;
        for (var i = 0; i < std.marks.length; i++) {
            if (value8h >= std.marks[i][0] - 1e-9) hit = { level: std.marks[i][0], kind: std.marks[i][1] };
        }
        return hit;
    }

    /** Сколько ещё часов при уровне L, пока доза не дойдёт до target % (100 — предел). */
    function remainingHours(id, dosePct, L, target) {
        var T = allowedHours(id, L);
        var left = (target == null ? 100 : target) - dosePct;
        if (left <= 0) return 0;
        if (!isFinite(T)) return Infinity;
        return left / 100 * T;
    }

    /** Доза, до которой поднимается показатель «отметка mark за 8 ч». */
    function doseAtMark(id, mark) {
        var std = get(id);
        if (std.metric === 'lex') return 100 * Math.pow(10, (mark - std.criterion) / 10);
        return 100 * Math.pow(2, (mark - std.criterion) / std.exchange);
    }

    /**
     * Живой счёт: секунды замера с их уровнями. Хранит уровни по секундам, чтобы
     * смена норматива пересчитывала ту же смену, а не начинала заново.
     */
    function createLive() {
        var levels = [], dts = [], energy = 0, time = 0;
        return {
            add: function (L, dt) {
                if (!(dt > 0) || !isFinite(L)) return;
                levels.push(L); dts.push(dt);
                energy += Math.pow(10, L / 10) * dt;
                time += dt;
            },
            reset: function () { levels = []; dts = []; energy = 0; time = 0; },
            seconds: function () { return time; },
            leq: function () { return time > 0 ? 10 * Math.log10(energy / time) : -Infinity; },
            dose: function (id) {
                var d = 0;
                for (var i = 0; i < levels.length; i++) d += partialDose(id, levels[i], dts[i] / 3600);
                return d;
            }
        };
    }

    return {
        STANDARDS: STANDARDS,
        ORDER: ORDER,
        get: get,
        defaultFor: defaultFor,
        allowedHours: allowedHours,
        partialDose: partialDose,
        doseOf: doseOf,
        eightHour: eightHour,
        verdict: verdict,
        remainingHours: remainingHours,
        doseAtMark: doseAtMark,
        createLive: createLive
    };
});
